import {
  type ColumnLayout,
  columnLayoutController,
  loadColumnLayout,
  localColumnLayoutStorage,
} from '@trm/ui-kit-admin';
import { create } from 'zustand';
import {
  clearedSelection,
  limitedSelection,
  selectMany,
  toggleSelected,
} from '~/components/entity-page/selection';

import { AccountOrderBy } from '../../../../../../generated/types';
import { ACCOUNTS_TABLE_ID, accountColumns } from './accountColumns';
import { parseAccountIdsCsv } from './accountIdsCsv';
import { canCommitAccountsSearch } from './accountsQuery';

export const ACCOUNT_PAGE_SIZE = 25;
export const MAX_SELECTED_ACCOUNTS = 200;
export const ACCOUNT_SEARCH_DEBOUNCE_MS = 300;

export type AccountIdFilter = { readonly ids: readonly number[]; readonly label: string };

interface AccountsPageState {
  search: string;
  committedSearch: string;
  searchVersion: number;
  idFilter: AccountIdFilter | null;
  csvError: string | null;
  page: number;
  orderBy: AccountOrderBy;
  selectedIds: ReadonlySet<number>;
  selectionError: string | null;
  selectionVersion: number;
  columnLayout: ColumnLayout;
  filters: {
    setSearch: (search: string) => void;
    submitSearch: () => void;
    uploadCsv: (file: File) => Promise<void>;
    clearIdFilter: () => void;
    setPage: (page: number) => void;
    setOrderBy: (orderBy: AccountOrderBy) => void;
  };
  selection: {
    toggle: (id: number) => void;
    setMany: (ids: readonly number[], selected: boolean) => void;
    clear: () => void;
  };
  openBulkAction: { id: string; ids: readonly number[]; run: number } | null;
  bulkAction: {
    open: (id: string) => void;
    close: () => void;
  };
  setColumnLayout: (layout: ColumnLayout) => void;
}

export const useAccountsPageStore = create<AccountsPageState>((set, get) => ({
  search: '',
  committedSearch: '',
  searchVersion: 0,
  idFilter: null,
  csvError: null,
  page: 1,
  orderBy: AccountOrderBy.RowIdDesc,
  selectedIds: new Set<number>(),
  selectionError: null,
  selectionVersion: 0,
  columnLayout: loadColumnLayout(ACCOUNTS_TABLE_ID, accountColumns, localColumnLayoutStorage),
  openBulkAction: null,
  filters: {
    setSearch: (search) => {
      const searchVersion = get().searchVersion + 1;
      set((state) => ({ search, searchVersion, page: 1, ...clearedSelection(state) }));
      if (canCommitAccountsSearch(search))
        setTimeout(() => {
          if (get().searchVersion === searchVersion)
            set((state) => ({ committedSearch: search, page: 1, ...clearedSelection(state) }));
        }, ACCOUNT_SEARCH_DEBOUNCE_MS);
    },
    submitSearch: () =>
      set((state) =>
        canCommitAccountsSearch(state.search)
          ? {
              committedSearch: state.search,
              searchVersion: state.searchVersion + 1,
              page: 1,
              ...clearedSelection(state),
            }
          : {},
      ),
    uploadCsv: async (file) => {
      const parsed = parseAccountIdsCsv(await file.text());
      set((state) =>
        parsed.kind === 'ids'
          ? {
              idFilter: { ids: parsed.ids, label: file.name },
              csvError: null,
              page: 1,
              ...clearedSelection(state),
            }
          : { csvError: parsed.error },
      );
    },
    clearIdFilter: () =>
      set((state) => ({ idFilter: null, csvError: null, page: 1, ...clearedSelection(state) })),
    setPage: (page) => set({ page }),
    setOrderBy: (orderBy) => set((state) => ({ orderBy, page: 1, ...clearedSelection(state) })),
  },
  selection: {
    toggle: (id) =>
      set((state) => ({
        ...limitedSelection(
          state.selectedIds,
          toggleSelected(state.selectedIds, id),
          MAX_SELECTED_ACCOUNTS,
          'accounts',
          `Select no more than ${MAX_SELECTED_ACCOUNTS} accounts.`,
        ),
        selectionVersion: state.selectionVersion + 1,
      })),
    setMany: (ids, selected) =>
      set((state) => ({
        ...limitedSelection(
          state.selectedIds,
          selectMany(state.selectedIds, ids, selected),
          MAX_SELECTED_ACCOUNTS,
          'accounts',
          `Select no more than ${MAX_SELECTED_ACCOUNTS} accounts.`,
        ),
        selectionVersion: state.selectionVersion + 1,
      })),
    clear: () => set(clearedSelection),
  },
  bulkAction: {
    open: (id) =>
      set((state) => ({
        openBulkAction: {
          id,
          ids: [...state.selectedIds],
          run: (state.openBulkAction?.run ?? 0) + 1,
        },
      })),
    close: () => set({ openBulkAction: null }),
  },
  setColumnLayout: (columnLayout) => {
    localColumnLayoutStorage.write(ACCOUNTS_TABLE_ID, columnLayout);
    set({ columnLayout });
  },
}));

export const clearAccountsSelection = () => useAccountsPageStore.getState().selection.clear();
export const useAccountColumnLayout = () => {
  const layout = useAccountsPageStore((state) => state.columnLayout);
  const setLayout = useAccountsPageStore((state) => state.setColumnLayout);
  return columnLayoutController(accountColumns, layout, setLayout);
};
